import { useState } from "react";
import { get_db_base, request_items } from "../../../../helpers/Functions";
import AttributesConfig from "../../../../shared/schema/comparisontool/Attributes";
import AudiencesConfig from "../../../../shared/schema/comparisontool/Audiences";
import BrandConfig from "../../../../shared/schema/comparisontool/Brand";
import ContactConfig from "../../../../shared/schema/comparisontool/Contact";
import CurrencyConfig from "../../../../shared/schema/comparisontool/Currency";
import DisclaimerConfig from "../../../../shared/schema/comparisontool/Disclaimer";
import FamilyConfig from "../../../../shared/schema/comparisontool/Family";
import GlobalSettingsConfig from "../../../../shared/schema/comparisontool/GlobalSettings";
import LocaleConfig from "../../../../shared/schema/comparisontool/Locale";
import PriceTypeConfig from "../../../../shared/schema/comparisontool/PriceType";
import ProductConfig from "../../../../shared/schema/comparisontool/Product";
import ProductTypeConfig from "../../../../shared/schema/comparisontool/ProductType";
import SpecificationStyleConfig from "../../../../shared/schema/comparisontool/SpecificationStyle";
import ClientInformationConfig from "./collections/ClientInformation";
import ConversionFactorsConfig from "./collections/ConversionFactors";
import ElectricityProfilesConfig from "./collections/ElectricityProfiles";
import FleetsConfig from "./collections/Fleets";
import InformationConfig from "./collections/Information";
import LocalesConfig from "./collections/Locales";
import MiscellaneousConfig from "./collections/Miscellaneous";
import PreviewExportConfig from "./collections/PreviewExport";
import ProductsConfig from "./collections/Products";
import ReportsConfig from "./collections/Reports";
import ReportTemplateConfig from "./collections/ReportTemplate";
import ReportTypesConfig from "./collections/ReportTypes";
import TecPpmLookupConfig from "./collections/TecPpmLookup";

function map_collection_items_by_id(collection, items) {
  let obj = {};
  obj.map = {};
  items.forEach((item) => (obj.map[item.id] = item));
  return obj;
}

const collateProjectData = (project, project_data) => {
  // Extract arrays and maps to array elements
  let collections = {
    _global: { project }
  };
  let collections_unmapped = {};
  let collections_maps = {};
  let collection_names = [];
  project_data.forEach((collection_info) => {
    collection_names.push(collection_info.name);
    collections[collection_info.name] = collection_info.data;
    collections_unmapped[collection_info.name] = collection_info.unmapped;
    collections_maps[collection_info.name] = collection_info.map;
  });

  // Perform mappings
  collection_names.forEach((collection_name) => {
    let next_collection = collections[collection_name];
    if (Array.isArray(next_collection))
      next_collection.forEach((item) =>
        item.map?.(collections, collections_maps)
      );
    else next_collection.map?.(collections, collections_maps);
  });

  return {
    collections,
    collections_maps,
    collection_names,
    collections_unmapped,
    config: {
      current_project: project
    }
  };
};

const useProjectData = (project) => {
  const [projectData, setPd] = useState(null);
  //eslint-disable-next-line
  const [pdLoading, setPdLoading] = useState(false);
  //eslint-disable-next-line
  const [pdError, setPdError] = useState(false);
  const db_base = get_db_base(project);

  const preview_collections_config = [
    GlobalSettingsConfig,
    BrandConfig,
    SpecificationStyleConfig,
    AttributesConfig,
    PriceTypeConfig,
    ContactConfig,
    CurrencyConfig,
    { ...LocaleConfig, collection: "locales" },
    AudiencesConfig,
    ProductConfig,
    ProductTypeConfig,
    FamilyConfig,
    DisclaimerConfig,
    ClientInformationConfig,
    FleetsConfig,
    MiscellaneousConfig,
    PreviewExportConfig,
    ReportsConfig,
    ReportTemplateConfig,
    ReportTypesConfig,
    InformationConfig,
    ProductsConfig,
    ElectricityProfilesConfig,
    LocalesConfig,
    TecPpmLookupConfig,
    ConversionFactorsConfig
  ];

  function request(
    collection,
    collection_name_override,
    is_single,
    fields_to_get,
    filter
  ) {
    return request_items(
      db_base,
      collection,
      collection_name_override,
      is_single,
      fields_to_get,
      filter
    );
  }

  function loadType(collection, fields, type, single, filter) {
    return new Promise((accept, reject) => {
      request(collection, null, single, fields, filter)
        .then((items) => {
          let obj = { name: collection };
          if (single) {
            obj.data = new type(items);
            obj.unmapped = items;
          } else {
            obj.data = items.map((item) => new type(item));
            obj.unmapped = items;
            let mapped_data = map_collection_items_by_id(collection, obj.data);
            obj = { ...obj, ...mapped_data };
          }

          accept(obj);
        })
        .catch((e) => reject(e));
    });
  }

  const load = (collection_configs) => {
    let promises = collection_configs.map(
      ({ collection, fields, adapter, single, filter }) =>
        loadType(collection, fields, adapter, single, filter)
    );

    return Promise.all(promises).then((project_data) => {
      const collated_data = collateProjectData(project, project_data);
      setPd(collated_data);
      return collated_data;
    });
  };

  const loadPreview = () => load(preview_collections_config);

  const status = { projectData, pdLoading, pdError };
  const actions = { loadPreview };
  return [status, actions];
};

export default useProjectData;
